/**
 * Google provider — the enrichment seam for Google Maps Platform.
 *
 * This module is deliberately thin in this sprint: it defines the exact
 * integration surface (Street View, Places, Routes, Geocoding, map canvas)
 * and safe URL/loader builders, so wiring live Google services later is a
 * key drop-in plus per-capability fills — no component rework.
 *
 * Guarantees:
 *  - Never throws when the key is absent: every builder returns null and
 *    enrichAreaContext returns the platform context unchanged.
 *  - The key is read from the environment at build time and never logged,
 *    rendered, or committed.
 *  - @react-google-maps/api (already a dependency) is only imported by the
 *    map-canvas component, which itself only mounts behind the design gate —
 *    core browsing never loads Google code.
 */

import { GOOGLE_MAPS_API_KEY } from './config';
import { AMENITY_CATEGORIES, computeDriveRoute, findNearby, formatKm, resolvePlaceCoords } from './googleClient';

/** Street View Static preview URL (photographic, backbone-compatible). */
export function buildStreetViewPreviewUrl({ lat, lng }, apiKey = GOOGLE_MAPS_API_KEY, size = '640x360') {
  if (!apiKey || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const params = new URLSearchParams({
    size,
    location: `${lat},${lng}`,
    fov: '80',
    key: apiKey,
  });
  return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
}

/**
 * Capability fillers — implemented in the live-integration sprint.
 * Each returns its input untouched today so callers can already depend on
 * the final shape: enrich(context) -> context with streetView/places/routes.
 */
export async function fetchNearbyPlaces(context) {
  const origin = context?.location;
  if (!origin || !Number.isFinite(origin.lat)) return context?.places || [];
  const headline = AMENITY_CATEGORIES.filter((c) => ['school', 'grocery', 'food'].includes(c.key));
  const results = await Promise.all(headline.map((cat) => findNearby(origin, cat, undefined, { max: 2 })));
  const places = [];
  headline.forEach((cat, i) => {
    (results[i] || []).forEach((p) =>
      places.push({
        id: p.id,
        name: p.name,
        category: cat.defaultLabel,
        distanceText: formatKm(p.distanceKm),
      }),
    );
  });
  return places;
}

export async function fetchRouteTimes(context) {
  const origin = context?.location;
  const bases = context?.bases || [];
  if (!origin || !Number.isFinite(origin.lat) || bases.length === 0) return context?.routes || [];
  const routes = await Promise.all(
    bases.slice(0, 2).map(async (base) => {
      const coords = await resolvePlaceCoords(base, origin);
      if (!coords) return null;
      const route = await computeDriveRoute(origin, coords);
      return route ? { destination: base, driveText: route.durationText } : null;
    }),
  );
  return routes.filter(Boolean);
}

/** Merge Google enrichment into a platform context (no-op without a key). */
export async function enrichAreaContext(platformContext, apiKey = GOOGLE_MAPS_API_KEY) {
  if (!apiKey || !platformContext) return platformContext;
  const { lat, lng } = platformContext.location || {};
  return {
    ...platformContext,
    mode: 'google',
    streetView: buildStreetViewPreviewUrl({ lat, lng }, apiKey),
    places: await fetchNearbyPlaces(platformContext),
    routes: await fetchRouteTimes(platformContext),
  };
}
